import {
  destinationCountriesByZone,
  getRegionName,
  getStreamingRegionName,
  iso3166,
  messages,
  type RegionCode
} from "@quortex/front-common"
import { isEmpty } from "lodash-es"

export const getWorldwideStreamingCountries = (): api.ott.Pool["streamingCountries"] => {
  return iso3166.en.map(([, , alpha3]) => alpha3) as api.ott.Pool["streamingCountries"]
}

export const isWorldwideStreamingCountries = (
  streamingCountries: api.ott.Pool["streamingCountries"]
): boolean => {
  const worldwideCountries = getWorldwideStreamingCountries()
  return streamingCountries.length === worldwideCountries.length
}

export const getStreamingCountriesDefaultValues = (
  streamingCountries: api.ott.Pool["streamingCountries"]
): {
  streamingCountries: api.ott.Pool["streamingCountries"]
  streamingCountriesRestriction: "worldwide" | "restricted"
} => {
  return {
    streamingCountries,
    streamingCountriesRestriction: isWorldwideStreamingCountries(streamingCountries)
      ? "worldwide"
      : "restricted"
  }
}

const formatListWithLimit = (itemList: readonly string[], limit = 5): string => {
  if (isEmpty(itemList)) return ""

  const firstItemList = itemList.slice(0, limit).join(", ")
  const remainingCount = itemList.length - limit

  return remainingCount > 0
    ? `${firstItemList} and ${remainingCount} other${remainingCount > 1 ? "s" : ""}`
    : `${firstItemList}`
}

export const formatStreamingRegionLabels = (
  streamingCountries: api.ott.Pool["streamingCountries"],
  limit = 5
): string => {
  if (isEmpty(streamingCountries)) return ""

  if (streamingCountries.length === 1) {
    return streamingCountries[0] ? getRegionName(streamingCountries[0]) : ""
  }

  if (isWorldwideStreamingCountries(streamingCountries)) {
    return "No restriction, available everywhere"
  }

  const regionsSet = new Set(streamingCountries)

  const fullyCoveredZones = (
    Object.entries(destinationCountriesByZone) as [
      keyof typeof destinationCountriesByZone,
      api.ott.Pool["streamingCountries"]
    ][]
  )
    .filter(([, countries]) => countries.every(country => regionsSet.has(country)))
    .map(([zone]) => zone)

  const zonesCountriesSet = new Set<RegionCode>(
    fullyCoveredZones.flatMap(zone => destinationCountriesByZone[zone])
  )

  const standaloneCountries = streamingCountries.filter(country => !zonesCountriesSet.has(country))

  const labels = [
    ...fullyCoveredZones.map(zone => messages.en.destination.zone[zone]),
    ...standaloneCountries.map(getRegionName)
  ]

  const sortedLabels = [...labels].sort((a, b) => a.localeCompare(b))

  return sortedLabels.length > limit ? formatListWithLimit(sortedLabels) : sortedLabels.join(", ")
}

export const getViewersRestrictionLabel = (
  streamingCountries: api.ott.Pool["streamingCountries"]
): string => {
  if (!streamingCountries?.length) {
    return ""
  }

  if (isWorldwideStreamingCountries(streamingCountries)) {
    return "Available worldwide"
  }

  return `Restricted to ${getStreamingRegionName(streamingCountries)}`
}
